import { InputAddon, InputGroup, InputWrapper } from '@eigtech/ui/components/input'
import { NumberInput } from '@eigtech/ui/components/number-input'
import { FC } from 'react'
import { FieldValues, useController } from 'react-hook-form'
import { WithFieldNameAndDefaultValueProps } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type Props<TFieldValues extends FieldValues = FieldValues> = {
  numberInputProps?: React.ComponentProps<typeof NumberInput>
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  leftElement?: React.ReactNode
  rightElement?: React.ReactNode
} & BaseProps<TFieldValues>

export type NumberInputFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameAndDefaultValueProps<Props<TFieldValues>, TFieldValues>

export function NumberInputField<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  numberInputProps,
  leftAddon,
  leftElement,
  rightAddon,
  rightElement,
  defaultValue,
  ...props
}: NumberInputFieldProps<TFieldValues>) {
  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    defaultValue,
  })

  return (
    <FieldControl control={control} label={label} name={name} {...props}>
      <InputGroup>
        {typeof leftAddon === 'string' ? <InputAddon>{leftAddon}</InputAddon> : leftAddon}

        <InputWrapper>
          {leftElement}

          <NumberInput
            {...field}
            disabled={isSubmitting}
            id={name}
            {...numberInputProps}
            value={field.value}
          />

          {rightElement}
        </InputWrapper>

        {typeof rightAddon === 'string' ? <InputAddon>{rightAddon}</InputAddon> : rightAddon}
      </InputGroup>
    </FieldControl>
  )
}

export type NumberInputFieldComponent<TFieldValues extends FieldValues = FieldValues> = FC<
  NumberInputFieldProps<TFieldValues>
>
